import {useQuery} from '@apollo/client';
import {Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import navigateToURL from 'lib/navigateToURL';
import {getTabParam, pushTabParam, setTabParam} from 'lib/urlTabParam';
import ModelHubInferenceBanner from 'model-hub/ModelHubInferenceBanner';
import ModelHubPredictionJobsTab from 'model-hub/prediction-jobs/ModelHubPredictionJobsTab';
import PredictionBatchesTab from 'model-hub/runs/PredictionBatchesTab';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import * as Colors from 'ui-library/Colors';
import ClickableIcon, {ClickableIconStyle} from 'ui-library/icons/ClickableIcon';
import SectionTabs from 'ui-library/SectionTabs';

import type {GetScientificModels} from './__generated/GetScientificModels';
import DataFirstTab from './data-first/DataFirstTab';
import {ScientificModelCard} from './ScientificModelCard';
import {GET_SCIENTIFIC_MODELS_QUERY} from './ScientificModelsQueries';

export const MODEL_HUB_TAB = {
  MODELS: 'models',
  DATA: 'data',
  PREDICTION_BATCHES: 'prediction-batches',
  PREDICTION_JOBS: 'prediction-jobs',
} as const;

type ModelHubTab = (typeof MODEL_HUB_TAB)[keyof typeof MODEL_HUB_TAB];

function isValidTab(tab: string | null, dataTabEnabled: boolean): tab is ModelHubTab {
  if (tab === MODEL_HUB_TAB.DATA && !dataTabEnabled) {
    return false;
  }
  return (
    tab === MODEL_HUB_TAB.MODELS ||
    tab === MODEL_HUB_TAB.DATA ||
    tab === MODEL_HUB_TAB.PREDICTION_BATCHES ||
    tab === MODEL_HUB_TAB.PREDICTION_JOBS
  );
}

function getInitialTab(dataTabEnabled: boolean): ModelHubTab {
  const urlTab = getTabParam();
  return isValidTab(urlTab, dataTabEnabled) ? urlTab : MODEL_HUB_TAB.MODELS;
}

interface PredictionJobNavState {
  predictionBatchId: string | null;
  predictionBatchName: string | null;
}

interface PredictionBatchNavState {
  predictionBatchId: string | null;
}

export function ScientificModelsView(): JSX.Element {
  const showDataTab = BenchlingConfig.MODEL_HUB_ENABLE_DATA_TAB;
  const [activeTab, setActiveTab] = React.useState<ModelHubTab>(() => getInitialTab(showDataTab));
  const [predictionJobNav, setPredictionJobNav] = React.useState<PredictionJobNavState>({
    predictionBatchId: null,
    predictionBatchName: null,
  });
  const [predictionBatchNav, setPredictionBatchNav] = React.useState<PredictionBatchNavState>({
    predictionBatchId: null,
  });

  const handleTabChange = useCallback((tab: ModelHubTab) => {
    setActiveTab(tab);
    pushTabParam(tab);
  }, []);

  const handleNavigateToPredictionJobs = useCallback((batchId: string, batchName: string) => {
    setPredictionJobNav({predictionBatchId: batchId, predictionBatchName: batchName});
    setActiveTab(MODEL_HUB_TAB.PREDICTION_JOBS);
    pushTabParam(MODEL_HUB_TAB.PREDICTION_JOBS);
  }, []);

  const handleNavigateToBatch = useCallback((batchId: string) => {
    setPredictionBatchNav({predictionBatchId: batchId});
    setActiveTab(MODEL_HUB_TAB.PREDICTION_BATCHES);
  }, []);

  // Clear nav state after it's been consumed so it doesn't re-apply on tab re-entry
  useEffect(() => {
    if (activeTab !== MODEL_HUB_TAB.PREDICTION_JOBS && predictionJobNav.predictionBatchId != null) {
      setPredictionJobNav({predictionBatchId: null, predictionBatchName: null});
    }
  }, [activeTab, predictionJobNav.predictionBatchId]);

  useEffect(() => {
    if (activeTab !== MODEL_HUB_TAB.PREDICTION_BATCHES && predictionBatchNav.predictionBatchId != null) {
      setPredictionBatchNav({predictionBatchId: null});
    }
  }, [activeTab, predictionBatchNav.predictionBatchId]);

  // Sync tab state with browser back/forward navigation
  useEffect(() => {
    const handlePopState = (): void => {
      const urlTab = getTabParam();
      if (isValidTab(urlTab, showDataTab)) {
        setActiveTab(urlTab);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [showDataTab]);

  const {data, loading, error} = useQuery<GetScientificModels>(GET_SCIENTIFIC_MODELS_QUERY);

  useEffect(() => {
    setTabParam(activeTab);
  }, [activeTab]);

  useEffect(() => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.MODEL_HUB_VIEWED);
  }, []);

  const renderModelsContent = (): JSX.Element => {
    if (loading) {
      return <Text>Loading models...</Text>;
    }

    if (error) {
      return <Text color="red">Error loading models: {error.message}</Text>;
    }

    const models = data?.scientificModels ?? [];

    return (
      <div className={css(styles.modelsGrid)}>
        {models.map((model) => (
          <ScientificModelCard
            key={model.systemName}
            systemName={model.systemName}
            displayName={model.displayName}
            iconPath={model.iconPath}
            description={model.description}
            paperLink={model.paperLink}
            inputTypes={model.inputTypes}
            category={model.category}
            onNavigateToRuns={() => {
              handleTabChange(MODEL_HUB_TAB.PREDICTION_BATCHES);
            }}
          />
        ))}
      </div>
    );
  };

  const tabs = useMemo(() => {
    const tabList = [
      {
        id: 'model-hub-models-tab',
        ariaControls: 'model-hub-models-panel',
        text: 'Models',
        isActive: activeTab === MODEL_HUB_TAB.MODELS,
        handleClick: () => {
          handleTabChange(MODEL_HUB_TAB.MODELS);
        },
      },
    ];

    if (showDataTab) {
      tabList.push({
        id: 'model-hub-data-tab',
        ariaControls: 'model-hub-data-panel',
        text: 'Data',
        isActive: activeTab === MODEL_HUB_TAB.DATA,
        handleClick: () => {
          handleTabChange(MODEL_HUB_TAB.DATA);
        },
      });
    }

    tabList.push(
      {
        id: 'model-hub-prediction-batches-tab',
        ariaControls: 'model-hub-prediction-batches-panel',
        text: 'Prediction Batches',
        isActive: activeTab === MODEL_HUB_TAB.PREDICTION_BATCHES,
        handleClick: () => {
          handleTabChange(MODEL_HUB_TAB.PREDICTION_BATCHES);
        },
      },
      {
        id: 'model-hub-prediction-jobs-tab',
        ariaControls: 'model-hub-prediction-jobs-panel',
        text: 'Prediction Jobs',
        isActive: activeTab === MODEL_HUB_TAB.PREDICTION_JOBS,
        handleClick: () => {
          handleTabChange(MODEL_HUB_TAB.PREDICTION_JOBS);
        },
      }
    );

    return tabList;
  }, [activeTab, handleTabChange, showDataTab]);

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.titleRow)}>
        <SplitBreadcrumbs crumbs={[<CrumbLink key="root" text="Model Hub" />]} />
        <ClickableIcon
          altText="Close"
          className={css(styles.closeButton)}
          icon="panel-hidden"
          iconStyle={ClickableIconStyle.NEGATIVE}
          onClick={() => navigateToURL('/editor')}
        />
      </div>
      <div className={css(styles.sectionTabs)}>
        <SectionTabs tabs={tabs} />
      </div>
      {activeTab === MODEL_HUB_TAB.MODELS && (
        <div
          id="model-hub-models-panel"
          role="tabpanel"
          aria-labelledby="model-hub-models-tab"
          className={css(styles.tabPanel)}
        >
          {renderModelsContent()}
        </div>
      )}
      {activeTab === MODEL_HUB_TAB.DATA && showDataTab && (
        <div
          id="model-hub-data-panel"
          role="tabpanel"
          aria-labelledby="model-hub-data-tab"
          className={css(styles.tabPanel)}
        >
          <DataFirstTab
            onNavigateToRuns={() => {
              handleTabChange(MODEL_HUB_TAB.PREDICTION_BATCHES);
            }}
          />
        </div>
      )}
      {activeTab === MODEL_HUB_TAB.PREDICTION_BATCHES && (
        <div
          id="model-hub-prediction-batches-panel"
          role="tabpanel"
          aria-labelledby="model-hub-prediction-batches-tab"
          className={css(styles.tabPanel)}
        >
          <PredictionBatchesTab
            onNavigateToModels={() => {
              handleTabChange(MODEL_HUB_TAB.MODELS);
            }}
            onNavigateToPredictionJobs={handleNavigateToPredictionJobs}
            initialPredictionBatchId={predictionBatchNav.predictionBatchId}
          />
        </div>
      )}
      {activeTab === MODEL_HUB_TAB.PREDICTION_JOBS && (
        <div
          id="model-hub-prediction-jobs-panel"
          role="tabpanel"
          aria-labelledby="model-hub-prediction-jobs-tab"
          className={css(styles.tabPanel)}
        >
          <ModelHubPredictionJobsTab
            initialPredictionBatchId={predictionJobNav.predictionBatchId}
            initialPredictionBatchLabel={predictionJobNav.predictionBatchName}
            onNavigateToBatch={handleNavigateToBatch}
          />
        </div>
      )}
      <div className={css(styles.stickyBannerWrapper)}>
        <ModelHubInferenceBanner />
      </div>
    </div>
  );
}

const BANNER_HEIGHT_PX = 52;

const styles = StyleSheet.create({
  container: {
    padding: `16px 24px ${BANNER_HEIGHT_PX}px 90px`,
    width: '100%',
    maxWidth: '100%',
  },
  stickyBannerWrapper: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    // Leave room on the right for the help widget in the bottom-right corner
    right: 90,
    // Match the page's horizontal padding so the banner aligns with the main content
    paddingLeft: 90,
    paddingRight: 24,
    zIndex: 100,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  closeButton: {
    margin: 10,
  },
  sectionTabs: {
    borderBottomColor: Colors.GRAY,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
  },
  tabPanel: {
    marginTop: 12,
  },
  modelsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    width: '100%',
  },
});
